import React, { useState, useEffect, useRef } from 'react';
import { companyAPI, superAdminAPI } from '../services/api';
import SuperAdminUsers from '../components/SuperAdminUsers';
import SuperAdminServices from '../components/SuperAdminServices';
import SuperAdminAnalytics from '../components/SuperAdminAnalytics';
import SuperAdminTickets from '../components/SuperAdminTickets';
import SuperAdminBroadcasts from '../components/SuperAdminBroadcasts';
import SuperAdminPromotionalApprovals from '../components/SuperAdminPromotionalApprovals';
import SuperAdminMarketingTemplates from '../components/SuperAdminMarketingTemplates';
import SuperAdminTokens from '../components/SuperAdminTokens';
import SuperAdminGlobalSettings from '../components/SuperAdminGlobalSettings';
import SuperAdminEmailIntegrations from '../components/SuperAdminEmailIntegrations';
import SuperAdminBikes from '../components/SuperAdminBikes';
import SuperAdminMarketingCampaigns from '../components/SuperAdminMarketingCampaigns';
import Layout from '../components/Layout';
import { getTabPillStateStyles } from '../utils/themeHelpers';
import Icon from '../components/Icon';

function SuperAdminDashboard({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [companyForm, setCompanyForm] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [appSettings, setAppSettings] = useState({ allow_company_registration: false });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('tickets');
  // App logo state
  const [appLogo, setAppLogo] = useState({ path: null, url: null });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      setError(`Access denied: Current user role is '${user?.role}', but 'super_admin' is required.`);
    } else {
      fetchCompanies();
      fetchAppSettings();
      fetchAppLogo();
    }
  }, [user]);

  const [pendingPromoCount, setPendingPromoCount] = useState(0);
  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    superAdminAPI.getPendingPromotionalEmails()
      .then(r => {
        const list = r.data.promotional_emails || [];
        setPendingPromoCount(list.filter(e => e.status === 'pending_approval').length);
      })
      .catch(() => {});
  }, [user, activeTab]);

  const fetchAppSettings = async () => {
    try {
      const response = await companyAPI.getAppSettings();
      setAppSettings(response.data);
    } catch (err) {
      console.error('Failed to load app settings:', err);
    }
  };

  const fetchAppLogo = async () => {
    try {
      const response = await companyAPI.getAppLogo();
      setAppLogo({
        path: response.data.app_logo_path,
        url: response.data.app_logo_url
      });
    } catch (err) {
      console.error('Failed to load app logo:', err);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, GIF, WebP or SVG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    const file = logoInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploadingLogo(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await companyAPI.uploadAppLogo(formData);
      setAppLogo({
        path: response.data.app_logo_path,
        url: response.data.app_logo_url
      });
      setLogoPreview(null);
      setSuccess('App logo uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);

      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the app logo?')) return;

    setUploadingLogo(true);
    setError('');

    try {
      await companyAPI.deleteAppLogo();
      setAppLogo({ path: null, url: null });
      setLogoPreview(null);
      setSuccess('App logo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleToggleCompanyRegistration = async () => {
    setLoadingSettings(true);
    try {
      const newValue = !appSettings.allow_company_registration;
      await companyAPI.updateAppSettings({ allow_company_registration: newValue });
      setAppSettings({ allow_company_registration: newValue });
      setSuccess(`Company registration ${newValue ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await companyAPI.getAllCompanies();
      let companiesList = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          companiesList = response.data;
        } else if (response.data.companies && Array.isArray(response.data.companies)) {
          companiesList = response.data.companies;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          companiesList = response.data.data;
        }
      }

      setCompanies(companiesList);

      if (companiesList.length === 0) {
        setError('No companies found in the database.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load companies';
      const statusCode = err.response?.status;

      if (statusCode === 403) {
        setError(`Access denied (403): ${errorMsg}`);
      } else if (statusCode === 401) {
        setError(`Authentication required (401): Please log in again.`);
      } else if (statusCode === 404) {
        setError(`API endpoint not found (404)`);
      } else {
        setError(`Failed to load companies: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivateToggle = async (companyId, currentStatus) => {
    try {
      await companyAPI.activateCompany(companyId, !currentStatus);
      setSuccess(`Company ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchCompanies();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company status');
    }
  };

  const handlePermanentDeleteCompany = async (company) => {
    if (!company?.id) return;

    const approved = window.confirm(
      `Permanently delete company "${company.name}"?\n\nThis also permanently deletes related users and company data. This cannot be undone.`
    );
    if (!approved) return;

    const typedName = window.prompt(
      `Type the company name to confirm permanent deletion:\n${company.name}`
    );
    if (typedName !== company.name) {
      setError('Company name confirmation did not match. Deletion cancelled.');
      return;
    }

    try {
      await companyAPI.deleteCompany(company.id);
      if (selectedCompany?.id === company.id) {
        setShowDetailsModal(false);
        setSelectedCompany(null);
        setCompanyDetails(null);
        setCompanyForm(null);
      }
      await fetchCompanies();
      setSuccess(`Company "${company.name}" permanently deleted.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to permanently delete company');
    }
  };

  const viewCompanyDetails = async (company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    try {
      const response = await companyAPI.getCompanyDetails(company.id);
      const details = response.data.company;
      setCompanyDetails(details);
      setCompanyForm({
        ...(details || {}),
        address: (details && details.address) || {},
        company_id: company.id
      });
    } catch (err) {
      setError('Failed to load company details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCompanyField = (field, value) => {
    setCompanyForm((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleCompanyAddressField = (field, value) => {
    setCompanyForm((prev) => ({
      ...(prev || {}),
      address: { ...(prev?.address || {}), [field]: value }
    }));
  };

  const handleSaveCompany = async () => {
    if (!companyForm || !companyForm.company_id) return;
    try {
      const payload = {
        company_id: companyForm.company_id,
        name: companyForm.name,
        email: companyForm.email,
        phone: companyForm.phone,
        address: companyForm.address,
        is_active: companyForm.is_active,
        subscription_tier: companyForm.subscription_tier,
        user_limit: companyForm.user_limit,
        booking_buffer_minutes: companyForm.booking_buffer_minutes ?? 0,
        online_booking_availability_percent: companyForm.online_booking_availability_percent ?? 100,
        min_booking_advance_days: companyForm.min_booking_advance_days ?? 0,
        max_booking_advance_days: companyForm.max_booking_advance_days ?? 30,
        technician_can_add_services: companyForm.technician_can_add_services !== false,
        technician_can_add_bikes: companyForm.technician_can_add_bikes === true
      };
      await companyAPI.updateCompanyInfo(payload);
      setSuccess('Company updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchCompanies();
      if (selectedCompany) {
        const refreshed = await companyAPI.getCompanyDetails(selectedCompany.id);
        setCompanyDetails(refreshed.data.company);
        setCompanyForm({
          ...(refreshed.data.company || {}),
          address: (refreshed.data.company && refreshed.data.company.address) || {},
          company_id: selectedCompany.id
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company');
    }
  };

  const handleGeocodeCompany = async () => {
    if (!selectedCompany || !selectedCompany.id) return;
    try {
      setLoadingDetails(true);
      await companyAPI.geocodeCompany(selectedCompany.id);
      setSuccess('Address geocoded successfully! GPS coordinates updated.');
      setTimeout(() => setSuccess(''), 3000);

      // Refresh company details
      const refreshed = await companyAPI.getCompanyDetails(selectedCompany.id);
      setCompanyDetails(refreshed.data.company);
      setCompanyForm({
        ...(refreshed.data.company || {}),
        address: (refreshed.data.company && refreshed.data.company.address) || {},
        company_id: selectedCompany.id
      });

      // Refresh companies list
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to geocode address');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Access denied screen
  if (user && user.role !== 'super_admin') {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle="Access Denied" darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
          <h2 style={{ color: '#f87171', margin: '0 0 12px 0' }}>Access Denied</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '8px' }}>
            You are not authorized to access this page.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Current role: <strong style={{ color: '#fbbf24' }}>{user?.role}</strong> |
            Required: <strong style={{ color: '#4A8C18' }}>super_admin</strong>
          </p>
          <button onClick={onLogout} className="btn btn-primary" style={{ marginTop: '20px' }}>
            Logout and login as Super Admin
          </button>
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'tickets', label: 'Support Tickets', iconName: 'help' },
    { id: 'companies', label: 'Companies', count: companies.length, iconName: 'companies' },
    { id: 'users', label: 'All Users', iconName: 'users' },
    { id: 'bikes', label: 'All Bikes', iconName: 'bike' },
    { id: 'services', label: 'All Services', iconName: 'service' },
    { id: 'analytics', label: 'Analytics', iconName: 'analytics' },
    { id: 'email-integrations', label: 'Email Integrations', iconName: 'email' },
    { id: 'settings', label: 'Global Settings', iconName: 'settings' },
    { id: 'broadcasts', label: 'Broadcasts', iconName: 'email' },
    { id: 'marketing-campaigns', label: 'Marketing Email', iconName: 'email' },
    { id: 'promos', label: 'Promo Approvals', iconName: 'success' },
    { id: 'templates', label: 'Email Templates', iconName: 'description' },
    { id: 'tokens', label: 'Invitations', iconName: 'key' },
  ];

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle="Super Admin Dashboard"
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      pageSubtitle="Manage all companies, users, bikes, services, and bookings"
    >
      <div className="dashboard-shell">
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          padding: '8px',
          background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 16px',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: '0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                ...getTabPillStateStyles(activeTab === tab.id, darkMode),
              }}
            >
              {tab.iconName && (
                <Icon
                  name={tab.iconName}
                  color={activeTab === tab.id ? "nav_active" : "nav_inactive"}
                  darkMode={darkMode}
                  size={18}
                />
              )}
              {tab.icon && <span>{tab.icon}</span>}
              <span>{tab.label}</span>
              {((tab.id === 'promos' && pendingPromoCount > 0) || (tab.id !== 'promos' && tab.count !== undefined)) && (
                <span style={{
                  background: (tab.id === 'promos' && pendingPromoCount > 0) ? '#f59e0b' : (activeTab === tab.id ? 'rgba(0,0,0,0.15)' : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')),
                  color: (tab.id === 'promos' && pendingPromoCount > 0) ? '#fff' : 'inherit',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px'
                }}>
                  {tab.id === 'promos' ? pendingPromoCount : tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}

        {/* Application Settings - Only on companies tab */}
        {activeTab === 'companies' && (
          <div style={{
            background: 'var(--bg-overlay-light)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="settings" color="primary" darkMode={darkMode} size={18} />
                Application Settings
              </span>
            </h3>

            {/* App Logo Management */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: 'rgba(61, 90, 32, 0.08)',
              border: '1px solid rgba(61, 90, 32, 0.2)',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--lime-light)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🖼️ App Logo
              </h4>
              <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                The main logo displayed across the application (sidebar, login page, etc.)
              </p>

              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Logo Preview */}
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '12px',
                  border: '2px dashed rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.03)'
                }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : appLogo.url ? (
                    <img src={appLogo.url} alt="App logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '32px', opacity: 0.3 }}>📷</span>
                  )}
                </div>

                {/* Upload Controls */}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoSelect}
                    style={{ display: 'none' }}
                    id="app-logo-upload"
                  />

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'rgba(255,255,255,0.8)',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      📁 Select Image
                    </button>

                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="btn btn-primary"
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {uploadingLogo ? (
                            <>
                              <Icon name="loading" color="primary" darkMode={darkMode} size={16} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Icon name="upload" color="primary" darkMode={darkMode} size={16} />
                              Upload
                            </>
                          )}
                        </span>
                      </button>
                    )}

                    {appLogo.url && !logoPreview && (
                      <button
                        type="button"
                        onClick={handleLogoDelete}
                        disabled={uploadingLogo}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#f87171',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon name="delete" color="error" darkMode={darkMode} size={16} />
                          Delete
                        </span>
                      </button>
                    )}
                  </div>

                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                    PNG, JPG, GIF, WebP, SVG. Max 5MB
                  </p>
                </div>
              </div>
            </div>

            {/* Company Registration Toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <p style={{ margin: '0', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                  New companies can register themselves
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  fontWeight: '600',
                  color: appSettings.allow_company_registration ? '#4A8C18' : '#f87171'
                }}>
                  {appSettings.allow_company_registration ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={handleToggleCompanyRegistration}
                  disabled={loadingSettings}
                  className={`btn ${appSettings.allow_company_registration ? 'btn-secondary' : 'btn-primary'}`}
                >
                  {loadingSettings ? 'Updating...' : (appSettings.allow_company_registration ? 'Disable' : 'Enable')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div style={{
            background: 'var(--bg-overlay-light)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h2 style={{ margin: 0, color: '#ffffff', fontSize: '18px' }}>All Companies</h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['all', 'active', 'inactive'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    style={{
                      padding: '6px 14px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      background: filterStatus === status ? 'var(--lime-primary)' : 'rgba(255,255,255,0.05)',
                      color: filterStatus === status ? '#0a0a1a' : 'rgba(255,255,255,0.6)'
                    }}
                  >
                    {status === 'all' ? `All (${companies.length})` :
                      status === 'active' ? `Active (${companies.filter(c => c.is_active).length})` :
                        `Pending Approval (${companies.filter(c => !c.is_active).length})`}
                  </button>
                ))}
                <button
                  onClick={fetchCompanies}
                  style={{
                    padding: '6px 14px',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    background: 'var(--bg-overlay-medium)',
                    color: 'rgba(255,255,255,0.6)'
                  }}
                >
                  ↻ Refresh
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading companies...</div>
            ) : companies.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: 'rgba(255,255,255,0.5)'
              }}>
                <Icon name="companies" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
                <p>No companies found</p>
                <button onClick={fetchCompanies} className="btn btn-primary" style={{ marginTop: '12px' }}>
                  Refresh
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      {['ID', 'Name', 'Email', 'Phone', 'Subscription', 'Status', 'Created', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '12px 8px',
                          textAlign: 'left',
                          color: 'rgba(255,255,255,0.5)',
                          fontWeight: '500',
                          fontSize: '12px',
                          textTransform: 'uppercase'
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {companies
                      .filter(c => {
                        if (filterStatus === 'active') return c.is_active;
                        if (filterStatus === 'inactive') return !c.is_active;
                        return true;
                      })
                      .map(company => (
                        <tr key={company.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.5)' }}>{company.id}</td>
                          <td style={{ padding: '12px 8px', color: '#ffffff', fontWeight: '500' }}>{company.name}</td>
                          <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.7)' }}>{company.email || 'N/A'}</td>
                          <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.7)' }}>{company.phone || 'N/A'}</td>
                          <td style={{ padding: '12px 8px' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: company.subscription_tier === 'enterprise' ? 'rgba(139, 92, 246, 0.15)' : 
                                         company.subscription_tier === 'pro' ? 'rgba(59, 130, 246, 0.15)' : 
                                         'rgba(255, 255, 255, 0.08)',
                              color: company.subscription_tier === 'enterprise' ? '#a78bfa' : 
                                     company.subscription_tier === 'pro' ? '#60a5fa' : 
                                     'rgba(255,255,255,0.6)'
                            }}>
                              {(company.subscription_tier || 'basic').charAt(0).toUpperCase() + (company.subscription_tier || 'basic').slice(1)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: company.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                              color: company.is_active ? '#4A8C18' : '#f87171'
                            }}>
                              {company.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                            {company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => viewCompanyDetails(company)}
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  background: 'rgba(59, 130, 246, 0.15)',
                                  color: '#60a5fa'
                                }}
                              >
                                Details
                              </button>
                              <button
                                onClick={() => handleActivateToggle(company.id, company.is_active)}
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  background: company.is_active ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                  color: company.is_active ? '#fbbf24' : '#4A8C18'
                                }}
                              >
                                {company.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handlePermanentDeleteCompany(company)}
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  background: 'rgba(239, 68, 68, 0.18)',
                                  color: '#fca5a5'
                                }}
                              >
                                Delete Permanently
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tickets' && <SuperAdminTickets companies={companies} darkMode={darkMode} />}
        {activeTab === 'users' && <SuperAdminUsers companies={companies} />}
        {activeTab === 'bikes' && <SuperAdminBikes companies={companies} darkMode={darkMode} />}
        {activeTab === 'services' && <SuperAdminServices companies={companies} />}
        {activeTab === 'analytics' && <SuperAdminAnalytics companies={companies} />}
        {activeTab === 'broadcasts' && <SuperAdminBroadcasts darkMode={darkMode} />}
        {activeTab === 'marketing-campaigns' && <SuperAdminMarketingCampaigns darkMode={darkMode} />}
        {activeTab === 'promos' && <SuperAdminPromotionalApprovals darkMode={darkMode} />}
        {activeTab === 'templates' && <SuperAdminMarketingTemplates darkMode={darkMode} />}
        {activeTab === 'tokens' && <SuperAdminTokens darkMode={darkMode} />}
        {activeTab === 'email-integrations' && <SuperAdminEmailIntegrations darkMode={darkMode} />}
        {activeTab === 'settings' && <SuperAdminGlobalSettings darkMode={darkMode} />}

        {/* Company Details Modal */}
        {showDetailsModal && selectedCompany && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: '24px'
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            <div
              style={{
                background: darkMode ? '#1a1a3e' : '#ffffff',
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                borderRadius: '20px',
                padding: '28px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '85vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? '#ffffff' : '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="companies" color="primary" darkMode={darkMode} size={20} />
                  {selectedCompany.name}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'
                  }}
                >
                  ×
                </button>
              </div>

              {loadingDetails ? (
                <div className="loading">Loading company details...</div>
              ) : companyDetails ? (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Name</label>
                      <input
                        value={companyForm?.name || ''}
                        onChange={(e) => handleCompanyField('name', e.target.value)}
                        className="form-input"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Email</label>
                      <input
                        value={companyForm?.email || ''}
                        onChange={(e) => handleCompanyField('email', e.target.value)}
                        className="form-input"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Phone</label>
                      <input
                        value={companyForm?.phone || ''}
                        onChange={(e) => handleCompanyField('phone', e.target.value)}
                        className="form-input"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Status</label>
                      <select
                        value={companyForm?.is_active ? 'active' : 'inactive'}
                        onChange={(e) => handleCompanyField('is_active', e.target.value === 'active')}
                        className="form-input"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Subscription</label>
                      <select
                        value={companyForm?.subscription_tier || 'basic'}
                        onChange={(e) => handleCompanyField('subscription_tier', e.target.value)}
                        className="form-input"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      >
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>User Limit</label>
                      <input
                        type="number"
                        value={companyForm?.user_limit ?? ''}
                        onChange={(e) => handleCompanyField('user_limit', e.target.value === '' ? null : parseInt(e.target.value))}
                        className="form-input"
                        placeholder="Unlimited"
                        style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                      />
                    </div>
                  </div>

                  <h4 style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '12px' }}>Subscription</h4>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                    <span style={{ marginRight: '12px' }}>Tier: <strong>{(companyForm?.subscription_tier || 'basic').charAt(0).toUpperCase() + (companyForm?.subscription_tier || 'basic').slice(1)}</strong></span>
                    <span>Status: <strong>{companyForm?.subscription_status || 'trial'}</strong></span>
                  </div>

                  <h4 style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '12px' }}>Booking settings</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Buffer (min)</label>
                      <input type="number" min="0" max="120" value={companyForm?.booking_buffer_minutes ?? 0} onChange={(e) => handleCompanyField('booking_buffer_minutes', parseInt(e.target.value) || 0)} className="form-input" style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Online avail. %</label>
                      <input type="number" min="0" max="100" value={companyForm?.online_booking_availability_percent ?? 100} onChange={(e) => handleCompanyField('online_booking_availability_percent', Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))} className="form-input" style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Min advance (days)</label>
                      <input type="number" min="0" max="30" value={companyForm?.min_booking_advance_days ?? 0} onChange={(e) => handleCompanyField('min_booking_advance_days', parseInt(e.target.value) || 0)} className="form-input" style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px' }}>Max advance (days)</label>
                      <input type="number" min="1" max="365" value={companyForm?.max_booking_advance_days ?? 30} onChange={(e) => handleCompanyField('max_booking_advance_days', Math.max(1, Math.min(365, parseInt(e.target.value) || 30)))} className="form-input" style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }} />
                    </div>
                  </div>

                  <h4 style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '12px' }}>Address</h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    {['street', 'city', 'postcode', 'country'].map(field => (
                      <div key={field}>
                        <label style={{ display: 'block', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '12px', marginBottom: '4px', textTransform: 'capitalize' }}>{field}</label>
                        <input
                          value={companyForm?.address?.[field] || ''}
                          onChange={(e) => handleCompanyAddressField(field, e.target.value)}
                          className="form-input"
                          style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#fff' : '#1a1a2e', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}
                        />
                      </div>
                    ))}
                  </div>

                  {companyDetails.stats && (
                    <>
                      <h4 style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '12px' }}>Statistics</h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '12px',
                        marginBottom: '24px'
                      }}>
                        {[
                          { label: 'Users', value: companyDetails.stats.user_count || 0, color: '#60a5fa' },
                          { label: 'Bikes', value: companyDetails.stats.bike_count || 0, color: '#4A8C18' },
                          { label: 'Services', value: companyDetails.stats.service_count || 0, color: '#fbbf24' },
                        ].map(stat => (
                          <div key={stat.label} style={{
                            background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            borderRadius: '12px',
                            padding: '16px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: stat.color }}>{stat.value}</div>
                            <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handlePermanentDeleteCompany(selectedCompany)}
                      className="btn btn-danger"
                    >
                      Delete Permanently
                    </button>
                    <button onClick={() => setShowDetailsModal(false)} className="btn btn-secondary">Close</button>
                    {companyForm?.address && (companyForm.address.street || companyForm.address.city) && (
                      <button
                        onClick={handleGeocodeCompany}
                        className="btn btn-secondary"
                        title="Update GPS coordinates based on address"
                      >
                        📍 Update GPS Location
                      </button>
                    )}
                    <button onClick={handleSaveCompany} className="btn btn-primary">Save Changes</button>
                  </div>
                </>
              ) : (
                <div className="error">Failed to load company details</div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
}

export default SuperAdminDashboard;
